// Import Lazy from React
import React, {lazy} from 'react';
import {Navigate, createBrowserRouter, RouterProvider, RouteObject, Outlet} from 'react-router-dom';
import MobileAppRequiredModal from '../components/modals/MobileAppRequiredModal';
import routeNames from './RouteNames';
import PublicLayout from "../components/layouts/public/PublicLayout";
import Requests from "../pages/community/outlets/Requests";
import {useAppSelector} from "../hooks/useAppSelector";
import RouteErrorBoundary from "../pages/Error/RouteErrorBoundary";

// Choir Routes
const Choir = lazy(() => import('../pages/choir/Choir'));
const BelongTo = lazy(() => import('../pages/choir/outlets/BelongTo/BelongTo'));
const CanJoin = lazy(() => import('../pages/choir/outlets/join/CanJoin'));
const JoinChoir = lazy(() => import('../pages/choir/outlets/join/JoinChoir'));
const PublicChoirPage = lazy(() => import('../pages/choir/outlets/join/PublicChoirPage'));
const ManageBy = lazy(() => import('../pages/choir/outlets/manage/ManageBy'));
const ChoirDetail = lazy(() => import('../pages/choir/choir/ChoirDetail'));
const ChoirRehearsals = lazy(() => import('../pages/choir/choir/rehearsals/ChoirRehearsals'));
const ChoirSetlists = lazy(() => import('../pages/choir/choir/setlists/ChoirSetlists'));
const ChoirSchedule = lazy(() => import('../pages/choir/choir/schedules/ChoirSchedule'));
const ChoirMembers = lazy(() => import('../pages/choir/choir/members/ChoirMembers'));
const InviteMembers = lazy(() => import('../pages/choir/choir/members/InviteMembers'));
const MembersList = lazy(() => import('../pages/choir/choir/members/MembersList'));
const RequestsList = lazy(() => import('../pages/choir/choir/members/RequestsList'));
const Badges = lazy(() => import('../pages/choir/choir/members/Badges'));
const ChoirFeed = lazy(() => import('../pages/choir/choir/feeds/ChoirFeed'));
const ChoirIntegrate = lazy(() => import('../pages/choir/choir/integrate/ChoirIntegrate'));
const ChoirRehearsalDetail = lazy(() => import('../pages/choir/choir/choir_rehearsal_detail/ChoirRehearsalDetail'));
const LiveRehearsalLayout = lazy(() => import('../pages/common/ChoirRehearse/LiveRehearsalLayout'));
const ChoirRehearsalSongList = lazy(() => import('../pages/choir/choir/choir_rehearsal_detail/ChoirRehearsalSongList'));
const AddMembers = lazy(() => import("../pages/choir/choir/members/AddMembers"));
const CreateChoir = lazy(() => import("../pages/choir/create/CreateChoir"));
const EditChoir = lazy(() => import("../pages/choir/choir/EditChoir"));
const Projects = lazy(() => import('../pages/projects/Projects'));
const ProjectDiscover = lazy(() => import('../pages/projects/outlets/ProjectDiscover'));
const MyProjects = lazy(() => import('../pages/projects/outlets/MyProjects'));
const MyProjectInvites = lazy(() => import('../pages/projects/outlets/MyProjectInvites'));
const MyProjectApplications = lazy(() => import('../pages/projects/outlets/MyProjectApplications'));
const CreateProject = lazy(() => import('../pages/projects/create/CreateProject'));
const EditProject = lazy(() => import('../pages/projects/project/EditProject'));
const ProjectDetail = lazy(() => import('../pages/projects/project/ProjectDetail'));
const ProjectOverview = lazy(() => import('../pages/projects/project/outlets/ProjectOverview'));
const ProjectApplications = lazy(() => import('../pages/projects/project/outlets/ProjectApplications'));
const ProjectApplicationDetail = lazy(() => import('../pages/projects/project/outlets/ProjectApplicationDetail'));
const ProjectMembers = lazy(() => import('../pages/projects/project/outlets/ProjectMembers'));
const ProjectBudget = lazy(() => import('../pages/projects/project/outlets/ProjectBudget'));
const ProjectAgreements = lazy(() => import('../pages/projects/project/outlets/ProjectAgreements'));
const ProjectAgreementDetail = lazy(() => import('../pages/projects/project/outlets/ProjectAgreementDetail'));
const ProjectInvites = lazy(() => import('../pages/projects/project/outlets/ProjectInvites'));
const ProfilePage = lazy(() => import('../pages/profile/ProfilePage'));
// Choir Rehearsal Isolation
const ChoirLyrics = lazy(() => import('../pages/common/ChoirRehearse/Lyrics/Lyrics'));
const ChoirSlides = lazy(() => import('../pages/common/ChoirRehearse/slides/Slides'));
const ChoirPractice = lazy(() => import('../pages/common/ChoirRehearse/practise/Practice'));
const DryRunPractice = lazy(() => import('../pages/common/ChoirRehearse/dry_run_practice/DryRunPractice'));
const ReviewPractise = lazy(() => import('../pages/common/ChoirRehearse/review_practise/ReviewPractise'));
const ReviewEntry = lazy(() => import('../pages/common/ChoirRehearse/review_practise/outlet/ReviewEntry'));
const Submissions = lazy(() => import('../pages/common/ChoirRehearse/review_practise/outlet/Submissions'));
const SongsList = lazy(() => import('../pages/common/ChoirRehearse/songs_list/SongsList'));

// Community Routes
const Community = lazy(() => import('../pages/community/Community'));
const Contributions = lazy(() => import('../pages/community/outlets/Contributions'));
const Contribution = lazy(() => import('../pages/community/outlets/Contribution'));
const ReelsHome = lazy(() => import('../pages/community/outlets/reels/ReelsHome'))
const Reels = lazy(() => import('../pages/community/outlets/reels/outlets/Reels'));
const ReelDetails = lazy(() => import('../pages/community/outlets/reels/outlets/ReelDetails'))
const Challenges = lazy(() => import('../pages/community/outlets/challenges/Challenges'));
const ChallengeDetails = lazy(() => import('../pages/community/outlets/challenges/ChallengeDetails'));
const ChallengeEditor = lazy(() => import('../pages/community/outlets/challenges/ChallengeEditor'));
const ChallengeLeaderboard = lazy(() => import('../pages/community/outlets/challenges/ChallengeLeaderboard'));
const Marketplace = lazy(() => import('../pages/marketplace/Marketplace'));
const MarketplaceDiscover = lazy(() => import('../pages/marketplace/outlets/MarketplaceDiscover'));
const MarketplaceSongDetail = lazy(() => import('../pages/marketplace/outlets/MarketplaceSongDetail'));
const MarketplacePurchases = lazy(() => import('../pages/marketplace/outlets/MarketplacePurchases'));
const MarketplaceListings = lazy(() => import('../pages/marketplace/outlets/MarketplaceListings'));
const MarketplaceOrders = lazy(() => import('../pages/marketplace/outlets/MarketplaceOrders'));
const MarketplaceOrderDetail = lazy(() => import('../pages/marketplace/outlets/MarketplaceOrderDetail'));
const MarketplaceEarnings = lazy(() => import('../pages/marketplace/outlets/MarketplaceEarnings'));
const MarketplaceCheckoutSuccess = lazy(() => import('../pages/marketplace/outlets/MarketplaceCheckoutSuccess'));
const MarketplaceCheckoutPending = lazy(() => import('../pages/marketplace/outlets/MarketplaceCheckoutPending'));
const MarketplaceCheckoutFailure = lazy(() => import('../pages/marketplace/outlets/MarketplaceCheckoutFailure'));

// Rehearsal Routes
const Rehearsals = lazy(() => import('../pages/rehearsal/Rehearsals'));
const All = lazy(() => import('../pages/rehearsal/outlets/All'));
const Rehearsed = lazy(() => import('../pages/rehearsal/outlets/Rehearsed'));
const New = lazy(() => import('../pages/rehearsal/outlets/New'));
const StartRehearsal = lazy(() => import('../pages/common/Rehearse/StartRehearsal'));
const Slides = lazy(() => import('../pages/common/Rehearse/slides/Slides'));
const Practice = lazy(() => import('../pages/common/Rehearse/practise/Practice'));
const Lyrics = lazy(() => import('../pages/common/Rehearse/Lyrics/Lyrics'));
const CreateRehearsal = lazy(() => import('../pages/choir/choir/rehearsals/CreateRehearsal'));
const Exercises = lazy(() => import('../pages/exercises/Exercises'));
const SkillTree = lazy(() => import('../pages/exercises/SkillTree'));
const SkillDetail = lazy(() => import('../pages/exercises/SkillDetail'));
const TrainingLayout = lazy(() => import('../pages/training/TrainingLayout'));
const TrainingHome = lazy(() => import('../pages/training/TrainingHome'));
const TrainingWorkspace = lazy(() => import('../pages/training/TrainingWorkspace'));
const TrainingCourse = lazy(() => import('../pages/training/TrainingCourse'));
const TrainingCohort = lazy(() => import('../pages/training/TrainingCohort'));
const TrainingAssignment = lazy(() => import('../pages/training/TrainingAssignment'));
const TrainingClassroom = lazy(() => import('../pages/training/TrainingClassroom'));
const ChoirTraining = lazy(() => import('../pages/training/ChoirTraining'));
const AcceptTeachingInvitation = lazy(() => import('../pages/training/AcceptTeachingInvitation'));
const TrainingFeatureGate = lazy(() => import('../pages/training/components/TrainingFeatureGate'));

// Auth pages
const Login = lazy(() => import('../pages/auth/Login'));
const Register = lazy(() => import('../pages/auth/Register'));
const ConfirmOTP = lazy(() => import('../pages/auth/ConfirmOTP'));
const ForgotPasswordReset = lazy(() => import('../pages/auth/ForgotPasswordReset'));
const Confirmation = lazy(() => import('../pages/common/Confirmation'));
const ForgotPasswordOTP = lazy(() => import('../pages/auth/ForgotPasswordOTP'));
const Waitlist = lazy(() => import('../pages/public/Waitlist'));
const PrivacyPolicy = lazy(() => import('../pages/public/PrivacyPolicy'));
const AccountDeletion = lazy(() => import('../pages/public/AccountDeletion'));
const ContactUs = lazy(() => import('../pages/public/ContactUs'));

// Dashboard Routes
const Home = lazy(() => import('../pages/dashboard/Home'));
const Popular = lazy(() => import('../pages/dashboard/outlets/Popular'));
const Search = lazy(() => import('../pages/dashboard/outlets/Search'))
const Error404 = lazy(() => import('../pages/Error/Error404'));
const Albums = lazy(() => import('../pages/dashboard/outlets/albums/Albums'));
const AlbumDetails = lazy(() => import('../pages/dashboard/outlets/albums/AlbumDetails'));
const Artists = lazy(() => import('../pages/dashboard/outlets/artists/Artists'));
const ArtistDetails = lazy(() => import('../pages/dashboard/outlets/artists/ArtistDetails'));
const ArtistPopular = lazy(() => import('../pages/dashboard/outlets/artists/components/ArtistPopular'));
const ArtistAlbums = lazy(() => import('../pages/dashboard/outlets/artists/components/ArtistAlbums'));
const Genres = lazy(() => import('../pages/dashboard/outlets/Genres'));

// Vocal Contributions Routes
// const VocalContributions = lazy(() => import('../pages/vocal_contributions/VocalContributions'));
// const PopularContributions = lazy(() => import('../pages/vocal_contributions/outlets/PopularContributions'));
// const NewContributions = lazy(() => import('../pages/vocal_contributions/outlets/NewContributions'));

// Uploads Routes
const Uploads = lazy(() => import('../pages/uploads/Uploads'));

// Request Songs Routes
const RequestSongs = lazy(() => import('../pages/request_songs/RequestSongs'));
const SongRequests = lazy(() => import('../pages/request_songs/outlets/SongRequests'));
const CreateSongRequest = lazy(() => import('../pages/request_songs/outlets/CreateSongRequest'));

// Account Information
const AccountInformation = lazy(() => import('../pages/account_information/AccountInformation'));
const AccountInformationHome = lazy(() => import('../pages/account_information/outlets/AccountInformationHome'))
const EditProfile = lazy(() => import('../pages/account_information/outlets/EditProfile'));
const ChangePassword = lazy(() => import('../pages/account_information/outlets/change_password/ChangePassword'));
const ChangePasswordVerify = lazy(() => import('../pages/account_information/outlets/change_password/ChangePasswordVerify'));
const ChangePasswordConfirm = lazy(() => import('../pages/account_information/outlets/change_password/ChangePasswordConfirm'));
const PasswordConfirmation = lazy(() => import("../pages/account_information/outlets/change_password/PasswordConfirmation"));
const AccountChallenges = lazy(() => import("../pages/account_information/outlets/AccountChallenges"));

// Payments
const UpdateCard = lazy(() => import("../pages/account_information/outlets/payment/UpdateCard"));
const AvailablePlans = lazy(() => import("../pages/account_information/outlets/plans/AvailablePlans"));
const ManagePlans = lazy(() => import("../pages/account_information/outlets/plans/ManagePlans"));

// Role Request
const RoleRequest = lazy(() => import('../pages/request_role/RoleRequest'));
const RequestRoleOptions = lazy(() => import('../pages/request_role/outlets/RequestRoleOptions'));
const RequestDirectorRole = lazy(() => import('../pages/request_role/outlets/RequestDirectorRole'));
const RequestWhitelistedRole = lazy(() => import('../pages/request_role/outlets/RequestWhitelistedRole'));

// Notifications
const Notifications = lazy(() => import('../pages/notifications/Notifications'));
const NotificationList = lazy(() => import("../pages/notifications/outlets/NotificationList"))
const Notification = lazy(() => import("../pages/notifications/outlets/Notification"));

// Transaction History
const TransactionHistory = lazy(() => import("../pages/transaction_history/TransactionHistory"));
const TransactionHistoryList = lazy(() => import("../pages/transaction_history/outlets/Transactions"));
const TransactionDetail = lazy(() => import("../pages/transaction_history/outlets/TransactionDetail"));

// Setlist
const Setlists = lazy(() => import('../pages/setlist/SetList'));
const UserSetLists = lazy(() => import('../pages/setlist/outlets/UserSetLists'));
const PublicSetlistExplore = lazy(() => import('../pages/setlist/outlets/PublicSetlistExplore'));
const OwnedSetlistDetail = lazy(() => import('../pages/setlist/OwnedSetlistDetail'));

// Public Harmony Page
const PublicHarmonyPage = lazy(() => import('../pages/public/PublicHarmonyPage'));
const PublicSchedulePage = lazy(() => import('../pages/public/PublicSchedulePage'));
const PublicSetLists = lazy(() => import('../pages/setlist/PublicSetlist'));
const AcceptChoirInvitation = lazy(() => import('../pages/public/AcceptChoirInvitation'));
const PublicCommunityThreadHandoff = lazy(() => import('../pages/public/PublicCommunityThreadHandoff'));
const PublicResourceHandoff = lazy(() => import('../pages/public/PublicResourceHandoff'));
const ClaimPublicChoir = lazy(() => import('../pages/public/ClaimPublicChoir'));

const WhitelistedMarketplaceGuard = ({children}: {children: React.ReactElement}) => {
    const user = useAppSelector(state => state.auth.user);

    if (!user?.whitelisted) {
        return <Navigate to="/account-information" replace/>;
    }

    return children;
};

// Create router
const Router = (): React.JSX.Element => {
    const routes: RouteObject[] = [
        {
            path: routeNames.signin,
            element: <Login/>
        },
        {
            path: routeNames.signinConfirm,
            element: <ConfirmOTP/>
        },
        {
            path: routeNames.signup,
            element: <Register/>,
        },
        {
            path: '/open/signup',
            element: <Register/>,
        },
        {
            path: routeNames.forgotPassword,
            element: <ForgotPasswordOTP/>,
        },
        {
            path: routeNames.forgotPasswordReset,
            element: <ForgotPasswordReset/>,
        },
        {
            path: routeNames.confirmationScreen,
            element: <Confirmation/>,
        },
        {
            path: routeNames.waitlist,
            element: <Waitlist/>,
        },
        {
            path: routeNames.privacyPolicy,
            element: <PrivacyPolicy/>
        },
        {
            path: routeNames.deleteAccount,
            element: <AccountDeletion/>
        },
        {
            path: routeNames.contactUs,
            element: <ContactUs/>
        },
        {
            path: routeNames.claimChoir,
            element: <ClaimPublicChoir/>
        },
        {
            path: routeNames.home,
            element: <Home/>,
            children: [
                {
                    path: "", element: <Popular/>
                },
                {
                    path: "popular", element: <Popular/>
                },
                {
                    path: "search", element: <Search/>
                },
                {
                    path: 'albums/',
                    element: <Albums/>
                },
                {
                    path: 'albums/:uuid',
                    element: <AlbumDetails/>
                },
                {
                    path: 'artists',
                    element: <Artists/>
                },
                {
                    path: 'artists/:uuid',
                    element: <ArtistDetails/>,
                    children: [
                        {
                            path: '',
                            element: <ArtistPopular/>
                        },
                        //TODO: Check why these are not working
                        {
                            path: 'artists/:uuid/popular',
                            element: <ArtistPopular/>
                        },
                        {
                            path: 'artists/:uuid/albums',
                            element: <ArtistAlbums/>
                        }
                    ]
                },
                {
                    path: 'genres',
                    element: <Genres/>
                },
                {
                    path: routeNames.noWhere,
                    element: <Error404/>,
                }
            ]
        },
        {
            path: routeNames.community,
            element: <Community/>,
            children: [
                {path: "", element: <Navigate to="challenges" replace/>},
                {path: "challenges", element: <Challenges/>},
                {path: "challenges/new", element: <ChallengeEditor/>},
                {path: "challenges/:challenge_uuid", element: <ChallengeDetails/>},
                {path: "challenges/:challenge_uuid/edit", element: <ChallengeEditor/>},
                {path: "challenges/:challenge_uuid/leaderboard", element: <ChallengeLeaderboard/>},
                {path: "requests", element: <Requests/>},
                {
                    path: 'contributions',
                    element: <Contributions/>
                },
                {
                    path: 'contributions/:group_id',
                    element: <Contribution/>
                },
                {
                    path: 'reels',
                    element: <ReelsHome/>,
                    children: [
                        {
                            path: '',
                            element: <Reels/>
                        },
                        {
                            path: ':reel_id',
                            element: <ReelDetails/>
                        }
                    ]
                },
            ]
        },
        {
            path: routeNames.marketplace,
            element: <Marketplace/>,
            children: [
                {
                    path: '',
                    element: <MarketplaceDiscover/>
                },
                {
                    path: 'purchases',
                    element: <Navigate to="/account-information/purchases" replace/>
                },
                {
                    path: 'listings',
                    element: <Navigate to="/account-information/listings" replace/>
                },
                {
                    path: 'orders',
                    element: <Navigate to="/account-information/orders" replace/>
                },
                {
                    path: 'orders/:order_id',
                    element: <Navigate to="/account-information/orders" replace/>
                },
                {
                    path: 'checkout/success',
                    element: <MarketplaceCheckoutSuccess/>
                },
                {
                    path: 'checkout/pending',
                    element: <MarketplaceCheckoutPending/>
                },
                {
                    path: 'checkout/failure',
                    element: <MarketplaceCheckoutFailure/>
                },
                {
                    path: ':song_id',
                    element: <MarketplaceSongDetail/>
                }
            ]
        },
        {
            path: routeNames.rehearsal,
            element: <Rehearsals/>,
            children: [
                {
                    path: '',
                    element: <All/>
                },
                {
                    path: 'rehearsed',
                    element: <Rehearsed/>
                },
                {
                    path: 'new',
                    element: <New/>
                },
                {
                    path: 'rehearse/:id',
                    element: <StartRehearsal/>,
                    children: [
                        {
                            path: '',
                            element: <Lyrics/>
                        },
                        {
                            path: 'lyrics',
                            element: <Lyrics/>
                        },
                        {
                            path: 'slides',
                            element: <Slides/>
                        },
                        {
                            path: 'practice',
                            element: <Practice/>
                        },
                        {
                            path: '*',
                            element: <Error404/>
                        }
                    ]
                }
            ]
        },
        {
            path: routeNames.exercises,
            element: <Exercises/>,
            children: [
                {
                    path: '',
                    element: <SkillTree/>
                },
                {
                    path: ':skillId',
                    element: <SkillDetail/>
                }
            ]
        },
        {
            path: routeNames.teaching,
            element: <TrainingLayout/>,
            children: [
                {path: '', element: <TrainingHome/>},
                {path: 'workspaces/:workspaceId', element: <TrainingWorkspace/>},
                {path: 'courses/:courseId', element: <TrainingCourse/>},
                {path: 'cohorts/:cohortId', element: <TrainingCohort/>},
                {path: 'assignments/:assignmentId', element: <TrainingAssignment/>},
                {path: 'sessions/:sessionId', element: <TrainingFeatureGate surface="sessions"><TrainingClassroom/></TrainingFeatureGate>},
            ]
        },
        {
            path: routeNames.choir,
            element: <Choir/>,
            children: [
                {
                    path: '',
                    element: <BelongTo/>
                },
                {
                    path: 'choirs-you-can-join',
                    element: <CanJoin/>
                },
                {
                    path: 'join/:uuid',
                    element: <JoinChoir/>
                },
                {
                    path: 'manged-by-you',
                    element: <ManageBy/>
                },
                {
                    path: ':choir_id',
                    element: <ChoirDetail/>,
                    children: [
                        {
                            path: '',
                            element: <ChoirRehearsals/>
                        },
                        {
                            path: 'rehearsals',
                            element: <ChoirRehearsals/>
                        },
                        {
                            path: 'setlists',
                            element: <ChoirSetlists/>
                        },
                        {
                            path: 'setlists/:setlist_id',
                            element: <OwnedSetlistDetail/>
                        },
                        {
                            path: 'schedule',
                            element: <ChoirSchedule/>
                        },
                        {
                            path: 'members',
                            element: <ChoirMembers/>,
                            children: [
                                {
                                    path: '',
                                    element: <MembersList/>
                                },
                                {
                                    path: 'requests',
                                    element: <RequestsList/>
                                },
                                {
                                    path: 'invitations',
                                    element: <InviteMembers/>
                                },
                                {
                                    path: 'badges/:user',
                                    element: <Badges/>
                                }
                            ]
                        },
                        {
                            path: 'invite',
                            element: <AddMembers/>
                        },
                        {
                            path: 'feeds',
                            element: <ChoirFeed/>
                        },
                        {
                            path: 'integrate',
                            element: <ChoirIntegrate/>
                        },
                        {
                            path: 'training',
                            element: <TrainingFeatureGate surface="choir"><ChoirTraining/></TrainingFeatureGate>
                        }
                    ]
                },
                {
                    path: 'create',
                    element: <CreateChoir/>
                },
                {
                    path: ':choir_id/edit',
                    element: <EditChoir/>
                },
                {
                    element: <LiveRehearsalLayout/>,
                    children: [
                        {
                            path: ':choir_id/rehearsal/:rehearsal_id',
                            element: <ChoirRehearsalSongList/>
                        },
                        {
                            path: ':choir_id/rehearsal/:rehearsal_id/dry-run/:dry_run_audio_id/*',
                            element: <DryRunPractice/>
                        },
                        {
                            path: ':choir_id/rehearsal/:id/:song_id',
                            element: <ChoirRehearsalDetail type={'choir'}/>,
                            children: [
                                {
                                    path: '',
                                    element: <ChoirLyrics/>
                                },
                                {
                                    path: 'slides',
                                    element: <ChoirSlides type={'choir'}/>
                                },
                                {
                                    path: 'practise',
                                    element: <ChoirPractice/>
                                },
                                {
                                    path: 'review',
                                    element: <ReviewPractise/>,
                                    children: [
                                        {
                                            path: '',
                                            element: <Submissions/>
                                        },
                                        {
                                            path: ':user_email',
                                            element: <ReviewEntry/>
                                        },
                                    ]
                                },
                                {
                                    path: 'songs',
                                    element: <SongsList/>
                                }
                            ]
                        },
                    ]
                },
                {
                    path: ':name/rehearsal/create',
                    element: <CreateRehearsal/>
                }
            ]
        },
        {
            path: routeNames.projects,
            element: <Projects/>,
            children: [
                {
                    path: '',
                    element: <ProjectDiscover/>
                },
                {
                    path: 'mine',
                    element: <MyProjects/>
                },
                {
                    path: 'invites',
                    element: <MyProjectInvites/>
                },
                {
                    path: 'applications',
                    element: <MyProjectApplications/>
                },
                {
                    path: 'create',
                    element: <CreateProject/>
                },
                {
                    path: ':project_id/edit',
                    element: <EditProject/>
                },
                {
                    path: ':project_id',
                    element: <ProjectDetail/>,
                    children: [
                        {
                            path: '',
                            element: <ProjectOverview/>
                        },
                        {
                            path: 'applications',
                            element: <ProjectApplications/>
                        },
                        {
                            path: 'applications/:application_id',
                            element: <ProjectApplicationDetail/>
                        },
                        {
                            path: 'members',
                            element: <ProjectMembers/>
                        },
                        {
                            path: 'budget',
                            element: <ProjectBudget/>
                        },
                        {
                            path: 'agreements/:agreement_id',
                            element: <ProjectAgreementDetail/>
                        },
                        {
                            path: 'agreements',
                            element: <ProjectAgreements/>
                        },
                        {
                            path: 'invites',
                            element: <ProjectInvites/>
                        }
                    ]
                }
            ]
        },
        {
            path: `${routeNames.profile}/:username`,
            element: <ProfilePage/>,
        },
        {
            path: routeNames.setlists,
            element: <Setlists/>,
            children: [
                {
                    path: '',
                    element: <UserSetLists/>
                },
                {
                    path: 'explore',
                    element: <PublicSetlistExplore/>
                },
                {
                    path: ':setlist_id',
                    element: <OwnedSetlistDetail/>
                }],
        },
        {
            path: routeNames.share,
            element: <PublicLayout/>,
            children: [
                {
                    path: 'setlist/:uuid',
                    element: <PublicSetLists/>
                },
                {
                    path: 'choirs/:uuid',
                    element: <PublicChoirPage/>
                },
                {
                    path: 'harmonies',
                    element: <PublicHarmonyPage/>
                },
                {
                    path: 'schedule',
                    element: <PublicSchedulePage/>
                },
            ]
        },
        {
            path: '/share',
            element: <PublicLayout/>,
            children: [
                {
                    path: 'resources/:uuid',
                    element: <PublicResourceHandoff/>
                },
                {
                    path: 'community/:uuid',
                    element: <PublicCommunityThreadHandoff/>
                }
            ]
        },
        {
            path: '/share/profiles/:username',
            element: <ProfilePage/>,
        },
        {
            path: '/share/projects/:project_id',
            element: <ProjectDetail/>,
            children: [
                {
                    path: '',
                    element: <ProjectOverview/>
                },
                {
                    path: 'applications',
                    element: <ProjectApplications/>
                },
                {
                    path: 'applications/:application_id',
                    element: <ProjectApplicationDetail/>
                },
                {
                    path: 'members',
                    element: <ProjectMembers/>
                },
                {
                    path: 'budget',
                    element: <ProjectBudget/>
                },
                {
                    path: 'agreements/:agreement_id',
                    element: <ProjectAgreementDetail/>
                },
                {
                    path: 'agreements',
                    element: <ProjectAgreements/>
                },
                {
                    path: 'invites',
                    element: <ProjectInvites/>
                }
            ]
        },
        {
            path: routeNames.acceptChoirInvitation,
            element: <AcceptChoirInvitation/>
        },
        {
            path: '/share/accept-choir-invitation',
            element: <AcceptChoirInvitation/>
        },
        {
            path: '/share/accept-teaching-invitation',
            element: <TrainingFeatureGate><AcceptTeachingInvitation/></TrainingFeatureGate>
        },
        // {
        //     path: routeNames.contributions,
        //     element: <VocalContributions/>,
        //     children: [
        //         {
        //             path: "",
        //             element: <PopularContributions/>
        //         },
        //         {
        //             path: "popular",
        //             element: <PopularContributions/>
        //         },
        //         {
        //             path: 'new',
        //             element: <NewContributions />
        //         }
        //     ]
        // },
        {
            path: routeNames.uploads,
            element: (
                <WhitelistedMarketplaceGuard>
                    <Uploads/>
                </WhitelistedMarketplaceGuard>
            ),
        },
        {
            path: routeNames.requestSongs,
            element: <RequestSongs/>,
            children: [
                {
                    path: '',
                    element: <SongRequests/>
                },
                {
                    path: 'create',
                    element: <CreateSongRequest/>
                },

            ]
        },
        {
            path: routeNames.accountInformation,
            element: <AccountInformation/>,
            children: [
                {
                    path: '',
                    element: <AccountInformationHome/>
                },
                {
                    path: 'edit-profile',
                    element: <EditProfile/>
                },
                {
                    path: 'change-password',
                    element: <ChangePassword/>
                },
                {
                    path: 'change-password-verify',
                    element: <ChangePasswordVerify/>
                },
                {
                    path: 'change-password-confirm',
                    element: <ChangePasswordConfirm/>
                },
                {
                    path: 'update-card',
                    element: <UpdateCard/>
                },
                {
                    path: 'available-plans',
                    element: <AvailablePlans/>
                },
                {
                    path: 'manage-plans',
                    element: <ManagePlans/>
                },
                {
                    path: 'confirmation',
                    element: <PasswordConfirmation/>
                },
                {
                    path: 'order-history',
                    element: <TransactionHistoryList/>
                },
                {
                    path: 'order-history/:id',
                    element: <TransactionDetail/>
                },
                {
                    path: 'purchases',
                    element: <MarketplacePurchases/>
                },
                {
                    path: 'listings',
                    element: (
                        <WhitelistedMarketplaceGuard>
                            <MarketplaceListings/>
                        </WhitelistedMarketplaceGuard>
                    )
                },
                {
                    path: 'uploads',
                    element: <Navigate to="/uploads" replace/>
                },
                {
                    path: 'earnings',
                    element: (
                        <WhitelistedMarketplaceGuard>
                            <MarketplaceEarnings/>
                        </WhitelistedMarketplaceGuard>
                    )
                },
                {
                    path: 'orders',
                    element: <MarketplaceOrders/>
                },
                {
                    path: 'orders/:order_id',
                    element: <MarketplaceOrderDetail/>
                },
                {
                    path: 'challenges',
                    element: <AccountChallenges/>
                },
            ]
        },
        // {
        //     path: routeNames.changePassword,
        //     element: <ChangePassword />
        // },
        {
            path: routeNames.roleRequest,
            element: <RoleRequest/>,
            children: [
                {
                    path: '',
                    element: <RequestRoleOptions/>
                },
                {
                    path: 'request-director',
                    element: <RequestDirectorRole/>
                },
                {
                    path: 'request-whitelisted',
                    element: <RequestWhitelistedRole/>
                },
            ]
        },
        {
            path: routeNames.notifications,
            element: <Notifications/>,
            children: [
                {
                    path: '',
                    element: <NotificationList/>
                },
                {
                    path: ':id',
                    element: <Notification/>
                }
            ]
        },
        {
            path: routeNames.transactionHistory,
            element: <TransactionHistory/>,
            children: [
                {
                    path: '',
                    element: <TransactionHistoryList/>
                },
                {
                    path: ':id',
                    element: <TransactionDetail/>
                }
            ]
        },
        {
            path: routeNames.noWhere,
            element: <Error404/>,
        },
    ];

    const router = createBrowserRouter([{
        element: <><Outlet/><MobileAppRequiredModal/></>,
        children: routes.map(route => ({
            ...route,
            errorElement: <RouteErrorBoundary/>
        })),
    }]);

    return <RouterProvider router={router}/>;
};

export default Router;
